import { useEffect, useState } from 'react';
import { useAuth } from '../context/authContext';
import { getOrganisation } from '../services/organisation.service';
import { ALL_ORGANISATIONS_ID } from '../utils/organisation';

// Lightweight per-session cache so the organisation isn't refetched on every mount.
let cache = { key: null, data: null };

export const clearOrganisationCache = () => {
    cache = { key: null, data: null };
};

/**
 * Fetches and caches the current user's organisation.
 *
 * @returns {{ organisation: object|null, loading: boolean }}
 */
export const useOrganisation = () => {
    const { user } = useAuth();
    // The "All Organisations" selection is a sentinel, not a real organisation,
    // so there is no single record to fetch.
    const orgId =
        String(user?.organisationId) === ALL_ORGANISATIONS_ID ? null : user?.organisationId;
    const token = user?.token;

    const [organisation, setOrganisation] = useState(() =>
        cache.key === orgId ? cache.data : null
    );
    const [loading, setLoading] = useState(() => cache.key !== orgId);

    useEffect(() => {
        let active = true;

        if (!orgId || !token) {
            setOrganisation(null);
            setLoading(false);
            return;
        }

        if (cache.key === orgId && cache.data) {
            setOrganisation(cache.data);
            setLoading(false);
            return;
        }

        const fetchOrganisation = async () => {
            setLoading(true);
            const response = await getOrganisation(orgId, token);
            if (!active) return;
            if (response && response.status === 200) {
                const org = response.data.data;
                cache = { key: orgId, data: org };
                setOrganisation(org);
            }
            setLoading(false);
        };

        fetchOrganisation();

        return () => {
            active = false;
        };
    }, [orgId, token]);

    return { organisation, loading };
};
